import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, BarChart, Bar, Legend, Radar, RadarChart, PolarGrid,
    PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { Leaf, Droplet, Car, Plane, FileText, Award } from 'lucide-react';

// Matte Colors
const MATTE_COLORS = {
    emerald: '#10b981', // matte green
    rose: '#f43f5e',    // matte red
    sky: '#0ea5e9',     // matte blue
    indigo: '#6366f1',  // matte purple
    amber: '#f59e0b',   // matte orange
    slate: '#64748b'    // matte gray
};

// Recycling vs landfill (% of monthly e-waste + ops waste stream)
const wasteData = [
    { month: 'Jan', recycling: 52, landfill: 48 },
    { month: 'Feb', recycling: 55, landfill: 45 },
    { month: 'Mar', recycling: 58, landfill: 42 },
    { month: 'Apr', recycling: 63, landfill: 37 },
    { month: 'May', recycling: 68, landfill: 32 },
    { month: 'Jun', recycling: 72, landfill: 28 },
];

// Daily water gallons — chilled water makeup dominates for 16-rack DC
const waterData = [
    { name: 'Cooling Tower', value: 480 },
    { name: 'Cleaning', value: 95 },
    { name: 'Sanitary', value: 45 }
];

const fleetData = [
    { name: 'Electric', value: 58, color: MATTE_COLORS.emerald },
    { name: 'Hybrid', value: 28, color: '#a78bfa' },
    { name: 'Petrol', value: 14, color: '#38bdf8' }
];

// Travel emissions intensity (kg CO2e / employee / month) — small ops team of ~12
const travelRadarData = [
    { subject: 'Commuting', A: 62, fullMark: 100 },
    { subject: 'Domestic Flight', A: 48, fullMark: 100 },
    { subject: 'International', A: 35, fullMark: 100 },
    { subject: 'Hotel', A: 42, fullMark: 100 },
    { subject: 'Rail', A: 28, fullMark: 100 },
    { subject: 'Rental Car', A: 22, fullMark: 100 },
];

// Paper (reams/mo) vs Digital workflows (% adoption)
const materialsData = [
    { month: 'Jan', paper: 18, digital: 62 },
    { month: 'Feb', paper: 16, digital: 68 },
    { month: 'Mar', paper: 14, digital: 74 },
    { month: 'Apr', paper: 11, digital: 78 },
    { month: 'May', paper: 9, digital: 81 },
    { month: 'Jun', paper: 7, digital: 86 },
];

const esgData = [
    { quarter: 'Q1', score: 82 },
    { quarter: 'Q2', score: 85 },
    { quarter: 'Q3', score: 87 },
    { quarter: 'Q4', score: 89 },
];

const SustainStatCard = ({ icon, title, value, subValue, change, trendColor }: any) => {
    return (
        <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-4 flex flex-col justify-between shadow-sm min-h-[120px]">
            <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2 text-zinc-400">
                    <div className={`p-1 rounded ${trendColor === 'red' ? 'bg-red-500/10 text-red-500' : trendColor === 'orange' ? 'bg-orange-500/10 text-orange-500' : 'bg-green-500/10 text-green-500'}`}>
                        {icon}
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider">{title}</span>
                </div>
                <div className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${trendColor === 'red' ? 'bg-red-500/20 text-red-500' : 'bg-green-500/20 text-green-500'}`}>
                    {change}
                </div>
            </div>
            <div className="flex items-baseline gap-1 mt-1">
                <span className="text-xl font-bold text-white leading-none">{value}</span>
                <span className="text-[9px] text-zinc-500">{subValue}</span>
            </div>
        </div>
    );
};

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#1a1d24] border border-[#2e323a] p-3 rounded-lg shadow-xl">
                <p className="text-zinc-400 text-xs mb-2">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 text-sm font-semibold text-white">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color || entry.fill }} />
                        {entry.name}: {entry.value}
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const SustainabilityOverview = () => {
    return (
        <div className="w-full h-full flex flex-col gap-4 text-white">
            {/* Top 6 Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-5">
                <SustainStatCard icon={<Leaf size={14} className="text-green-500" />} title="Total Waste" value="2.4" subValue="Tons/Month" change="✓ -8.2%" trendColor="green" />
                <SustainStatCard icon={<Droplet size={14} className="text-blue-500" />} title="Water Usage" value="620" subValue="Gallons/Day" change="✓ -2.4%" trendColor="green" />
                <SustainStatCard icon={<Car size={14} className="text-indigo-400" />} title="Fleet Efficiency" value="58%" subValue="EV Share" change="✓ +5.1%" trendColor="green" />
                <SustainStatCard icon={<Plane size={14} className="text-orange-400" />} title="Travel Emissions" value="1.2" subValue="Tons CO2" change="↑ +0.8%" trendColor="red" />
                <SustainStatCard icon={<FileText size={14} className="text-cyan-400" />} title="Digital Usage" value="86%" subValue="Paper Reduction" change="✓ +3.2%" trendColor="green" />
                <SustainStatCard icon={<Award size={14} className="text-purple-400" />} title="ESG Score" value="89" subValue="/ 100" change="✓ +2.1%" trendColor="green" />
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Waste Management Trend */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex flex-col min-h-[320px]">
                    <h3 className="font-bold text-xs mb-4 flex items-center gap-2"><Leaf size={14} className="text-green-400" /> Waste Management Trend</h3>
                    <div className="flex-1 w-full relative -ml-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={wasteData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#2e323a" vertical={false} />
                                <XAxis dataKey="month" stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <YAxis stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '9px' }} verticalAlign="top" iconType="circle" />
                                <Area type="monotone" name="Recycling (Tons)" dataKey="recycling" stroke={MATTE_COLORS.emerald} strokeWidth={2} fillOpacity={0.6} fill={MATTE_COLORS.emerald} dot={{ r: 3, fill: MATTE_COLORS.emerald, strokeWidth: 0 }} />
                                <Area type="monotone" name="Landfill (Tons)" dataKey="landfill" stroke={MATTE_COLORS.amber} strokeWidth={2} fillOpacity={0.4} fill={MATTE_COLORS.amber} dot={{ r: 3, fill: MATTE_COLORS.amber, strokeWidth: 0 }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Water Usage Breakdown */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex flex-col min-h-[320px]">
                    <h3 className="font-bold text-xs mb-4 flex items-center gap-2"><Droplet size={14} className="text-blue-400" /> Water Usage Breakdown</h3>
                    <div className="flex-1 w-full relative">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart layout="vertical" data={waterData} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#2e323a" horizontal={true} vertical={false} />
                                <XAxis type="number" stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <YAxis dataKey="name" type="category" stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} width={80} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="value" fill={MATTE_COLORS.sky} radius={[0, 4, 4, 0]} barSize={24}>
                                    {waterData.map((_, index) => (
                                        <Cell key={`cell-${index}`} fill={index === 0 ? '#0ea5e9' : index === 1 ? '#60a5fa' : '#93c5fd'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Fleet Transition */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex flex-col min-h-[320px]">
                    <h3 className="font-bold text-xs mb-4 flex items-center gap-2"><Car size={14} className="text-indigo-400" /> Fleet Transition</h3>
                    <div className="flex-1 w-full relative">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={fleetData}
                                    innerRadius={0}
                                    outerRadius={65}
                                    dataKey="value"
                                    stroke="#1a1d24"
                                    strokeWidth={2}
                                >
                                    {fleetData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '9px' }} verticalAlign="bottom" iconType="circle" />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Travel Emissions Profile */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex flex-col min-h-[340px]">
                    <h3 className="font-bold text-xs mb-2 flex items-center gap-2"><Plane size={14} className="text-amber-400" /> Travel Emissions Profile</h3>
                    <div className="flex-1 w-full relative">
                        <ResponsiveContainer width="100%" height="100%">
                            <RadarChart cx="50%" cy="50%" outerRadius={70} data={travelRadarData}>
                                <PolarGrid stroke="#2e323a" />
                                <PolarAngleAxis dataKey="subject" tick={{ fill: '#71717a', fontSize: 9 }} />
                                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#71717a', fontSize: 8 }} />
                                <Radar name="Travel Profile" dataKey="A" stroke={MATTE_COLORS.amber} fill={MATTE_COLORS.amber} fillOpacity={0.4} />
                                <Tooltip />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Materials Usage Shift */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex flex-col min-h-[340px]">
                    <h3 className="font-bold text-xs mb-2 flex items-center gap-2"><FileText size={14} className="text-blue-400" /> Materials Usage Shift</h3>
                    <div className="flex-1 w-full relative mt-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={materialsData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#2e323a" vertical={false} />
                                <XAxis dataKey="month" stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <YAxis stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '9px', paddingBottom: '10px' }} verticalAlign="top" iconType="circle" />
                                <Bar name="Paper (Tons)" dataKey="paper" fill={MATTE_COLORS.rose} barSize={24} radius={[2, 2, 0, 0]} />
                                <Bar name="Digital (Units)" dataKey="digital" fill={MATTE_COLORS.sky} barSize={24} radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Row 4 */}
            <div className="grid grid-cols-1 gap-5">
                {/* ESG Score Progress */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex flex-col min-h-[280px]">
                    <h3 className="font-bold text-xs mb-4 flex items-center gap-2"><Award size={14} className="text-purple-400" /> ESG Score Progress</h3>
                    <div className="flex-1 w-full relative -ml-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={esgData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#2e323a" vertical={false} horizontal={false} />
                                <XAxis dataKey="quarter" stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <YAxis domain={[0, 100]} stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '9px' }} verticalAlign="top" iconType="circle" />
                                <Area
                                    type="monotone"
                                    name="ESG Score"
                                    dataKey="score"
                                    stroke={MATTE_COLORS.emerald}
                                    strokeWidth={2}
                                    fillOpacity={0.15}
                                    fill={MATTE_COLORS.emerald}
                                    dot={{ r: 4, fill: MATTE_COLORS.emerald, strokeWidth: 0 }}
                                    activeDot={{ r: 6 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default SustainabilityOverview;
